/**
 * Boom targeting (M10 P5a12c; plan 2026-10-17-m10-p5a §3.3, Q11): the helm marks up to four rocks with the Mining-mode
 * target key and the hull's mining booms work them on their own. Each boom takes a marked rock inside its arc
 * (`mining.json booms.arcDeg`, the full arc about the nose) and reach (`booms.rangeM`, surface distance from the boom
 * head): the least-shared mark first, then the nearest, so two marks share four booms 2/2 and one mark feeds every
 * boom that sees it. A boom with no marked rock in its arc idles. Pure and allocation-free (D-014).
 */
import { MAX_MINING_BOOMS } from '../data/schemas'

/** Marks the helm can hold at once (one per boom). */
export const MAX_BOOM_MARKS = MAX_MINING_BOOMS

/** `toggleMark` results. */
export const MARK_RESULT = { added: 1, removed: 2, full: 3 } as const

export interface BoomMarks {
  /** Nav kind (`NAV.rock` / `NAV.fragment`) per mark slot; slots `0..count-1` are live, packed. */
  readonly kind: Int8Array
  readonly index: Int32Array
  count: number
}

export function createBoomMarks(): BoomMarks {
  return { kind: new Int8Array(MAX_BOOM_MARKS), index: new Int32Array(MAX_BOOM_MARKS), count: 0 }
}

/** The slot holding (kind, index), else -1. */
export function markSlot(m: BoomMarks, kind: number, index: number): number {
  for (let i = 0; i < m.count; i++) if (m.kind[i] === kind && m.index[i] === index) return i
  return -1
}

/** Removes mark `slot`, keeping the rest packed in order. */
export function dropMark(m: BoomMarks, slot: number): void {
  if (slot < 0 || slot >= m.count) return
  for (let i = slot; i < m.count - 1; i++) {
    m.kind[i] = m.kind[i + 1]
    m.index[i] = m.index[i + 1]
  }
  m.count--
}

export function clearMarks(m: BoomMarks): void {
  m.count = 0
}

/** The target key on a boom hull: marks (kind, index), or unmarks it when already marked. `full` at four marks. */
export function toggleMark(m: BoomMarks, kind: number, index: number): number {
  const slot = markSlot(m, kind, index)
  if (slot >= 0) {
    dropMark(m, slot)
    return MARK_RESULT.removed
  }
  if (m.count >= MAX_BOOM_MARKS) return MARK_RESULT.full
  m.kind[m.count] = kind
  m.index[m.count] = index
  m.count++
  return MARK_RESULT.added
}

/**
 * Assigns each of `n` booms a mark slot into `out` (-1 = idle). `heads`: boom head world positions, 3 per boom;
 * `centres`/`radii`: each mark's world centre (3 per mark) and radius; `usable[j]` 0 skips mark j; (nx, ny, nz) the
 * unit nose. A mark is in boom k's reach when its surface is within `rangeM` of the head and the direction to its
 * centre lies within `arcDeg / 2` of the nose. Among those, the mark with the fewest booms so far wins, then the
 * nearest. `load` is scratch (≥ `MAX_BOOM_MARKS`).
 */
export function assignBooms(
  n: number,
  heads: Float64Array,
  nx: number,
  ny: number,
  nz: number,
  marks: number,
  centres: Float64Array,
  radii: Float64Array,
  usable: Uint8Array,
  rangeM: number,
  cosHalfArc: number,
  load: Uint8Array,
  out: Int8Array,
): void {
  for (let j = 0; j < MAX_BOOM_MARKS; j++) load[j] = 0
  for (let k = 0; k < n; k++) {
    out[k] = -1
    const hx = heads[k * 3]
    const hy = heads[k * 3 + 1]
    const hz = heads[k * 3 + 2]
    let best = -1
    let bestLoad = 0
    let bestDist = 0
    for (let j = 0; j < marks; j++) {
      if (usable[j] === 0) continue
      const dx = centres[j * 3] - hx
      const dy = centres[j * 3 + 1] - hy
      const dz = centres[j * 3 + 2] - hz
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz)
      if (dist - radii[j] > rangeM) continue
      if (dist > 1e-9 && (dx * nx + dy * ny + dz * nz) / dist < cosHalfArc) continue
      if (best < 0 || load[j] < bestLoad || (load[j] === bestLoad && dist < bestDist)) {
        best = j
        bestLoad = load[j]
        bestDist = dist
      }
    }
    if (best >= 0) {
      out[k] = best
      load[best]++
    }
  }
}
